/* eslint-disable no-undef */

import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button as AntButton,
  App,
  Card,
  Dropdown,
  MenuProps,
  Modal,
  Spin,
  Table,
} from "antd";
import { ColumnsType } from "antd/es/table";


import { ReactComponent as Plus } from "../../../assets/add.svg";
import { ReactComponent as Ellipsis } from "../../../assets/ellipsis.svg";
import { ReactComponent as Search } from "../../../assets/search.svg";
import { SearchInput } from "../../../custom";
import Button from "../../../custom/button/button";
import { usePagination } from "../../../hooks/usePagination";
import { useSearchTerms } from "../../../hooks/useSearchTerms";
import {
  deleteDepartment,
  getAllCategory,
  getAllPrograms,
} from "../../../requests";
import DeleteModalContent from "../../deleteModal/deleteModal";
import styles from "../styles.module.scss";

import { CreateDepartment, EditDepartment } from "./setup";

const Department = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [department, setDepartment] = useState<Program>({} as Program);

  const { currentPage, onChange } = usePagination();
  const { searchTerm, handleSearch } = useSearchTerms();

  const { notification } = App.useApp();

  const deleteDepartmentMutation = useMutation({
    mutationFn: deleteDepartment,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["get-department", currentPage],
    queryFn: () => getAllPrograms(currentPage, 10),
  });

  const facultyQuery = useQuery({
    queryKey: ["get-faculty"],
    queryFn: getAllCategory,
  });

  const columns: ColumnsType<Program> = [
    {
      key: "name",
      title: "Department name",
      dataIndex: "name",
    },
    {
      key: "facultyName",
      title: "Faculty",
      dataIndex: "facultyName",
    },
    {
      key: "code",
      title: "Code",
      dataIndex: "code",
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "activeStatus",
      render: (_, { activeStatus }) => (activeStatus ? "Active" : "Inactive"),
    },
    {
      key: "action",
      title: "",
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "1",
            label: "Edit",
            onClick: () => {
              setDepartment(record);
              setOpenEdit(true);
            },
          },
          {
            key: "2",
            label: "Delete",
            onClick: () => {
              setDepartment(record);
              setOpenDelete(true);
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <AntButton type="text" icon={<Ellipsis />} />
          </Dropdown>
        );
      },
    },
  ];

  const handleDeleteDepartment = async (department: Program) => {
    try {
      await deleteDepartmentMutation.mutateAsync(department.id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          refetch();
          setOpenDelete(false);
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const departments = data?.data as Program[];

  const filteredData = departments?.filter((item) =>
    item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  const faculties = facultyQuery.data?.data as Category[];

  if (isLoading) {
    return <Spin />;
  }

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  return (
    <div>
      <section className="space-between">
        <h3>Department Setup</h3>

        <Button
          onClick={() => setOpen(true)}
          iconBefore={<Plus />}
          text="Setup"
        />
      </section>

      <br />

      <Card bordered={false}>
        <div className={styles.inside}>
          <p>
            Showing 1-{filteredData?.length} of {departments?.length}
          </p>

          <div>
            {!showSearch && (
              <span>
                <Search
                  onClick={() => setShowSearch((showSearch) => !showSearch)}
                />
              </span>
            )}
            {showSearch && (
              <SearchInput value={searchTerm} onChange={handleSearch} />
            )}
          </div>
        </div>

        <Table
          dataSource={filteredData}
          columns={columns}
          pagination={{
            position: ["bottomCenter"],
            current: currentPage,
            total: data?.totalSize,
            onChange: onChange,
            pageSize: 10,
          }}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        centered
        title="Create Department"
        footer={null}
      >
        <CreateDepartment
          handleClose={() => setOpen(false)}
          faculties={faculties}
        />
      </Modal>

      <Modal
        open={openEdit}
        onCancel={() => setOpenEdit(false)}
        centered
        title="Edit Department"
        footer={null}
      >
        <EditDepartment
          item={department}
          handleClose={() => setOpenEdit(false)}
          faculties={faculties}
        />
      </Modal>

      <Modal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        centered
        title="Delete Department"
        footer={null}
      >
        <DeleteModalContent
          handleCloseModal={() => setOpenDelete(false)}
          handleSubmit={() => handleDeleteDepartment(department)}
          isLoading={deleteDepartmentMutation.isPending}
          title={department.name}
        />
      </Modal>
    </div>
  );
};

export default Department;
